/**
 * Real-user page-load metrics, reported once per page load.
 *
 * Built on the browser's own PerformanceObserver rather than the `web-vitals`
 * package so it adds no dependency. Values are sent as App Insights custom
 * metrics (`customMetrics` table), one row per metric per page load, so p75
 * per route is a single query:
 *
 *   customMetrics
 *   | where name startswith "WebVitals_"
 *   | summarize p75 = percentile(value, 75), n = count()
 *       by name, route = tostring(customDimensions.route)
 *
 * Reported when the page is first hidden (tab switch, close, navigation
 * away), which is the last moment every metric is final and the only one
 * the browser reliably delivers on.
 *
 * INP here is the slowest interaction seen, which equals the standard
 * definition for pages with fewer than 50 interactions and overstates it
 * slightly above that.
 */

export type VitalName = "TTFB" | "FCP" | "LCP" | "CLS" | "INP";

export type VitalReporter = (
  name: VitalName,
  value: number,
  properties: Record<string, string>,
) => void;

interface LayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

interface EventTimingEntry extends PerformanceEntry {
  interactionId?: number;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
const OPAQUE_ID = /^(?=.*\d)[A-Za-z0-9_-]{16,}$/;

/**
 * The route a metric is grouped by: the pathname with every id-like segment
 * replaced by `:id`. Query strings and hashes are dropped, so no tokens,
 * search terms or instance ids leave the browser, and per-applicant URLs
 * group together instead of each being its own row.
 */
export function routeTemplate(pathname: string): string {
  const segments = pathname
    .split("/")
    .filter(Boolean)
    .map((segment) =>
      UUID.test(segment) || NUMERIC.test(segment) || OPAQUE_ID.test(segment) ? ":id" : segment,
    );
  return `/${segments.join("/")}`;
}

/**
 * Cumulative Layout Shift as the web-vitals definition has it: shifts are
 * grouped into session windows (a new window starts after a 1 s gap or once
 * a window spans 5 s), shifts right after user input are ignored, and the
 * score is the largest window.
 */
export function clsFromShifts(
  shifts: ReadonlyArray<{ value: number; startTime: number; hadRecentInput: boolean }>,
): number {
  let max = 0;
  let windowValue = 0;
  let windowStart = 0;
  let last = 0;
  for (const shift of shifts) {
    if (shift.hadRecentInput) continue;
    if (windowValue > 0 && shift.startTime - last < 1000 && shift.startTime - windowStart < 5000) {
      windowValue += shift.value;
    } else {
      windowValue = shift.value;
      windowStart = shift.startTime;
    }
    last = shift.startTime;
    max = Math.max(max, windowValue);
  }
  return max;
}

function observe(type: string, callback: (entries: PerformanceEntry[]) => void, extra = {}): void {
  if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return;
  try {
    new PerformanceObserver((list) => callback(list.getEntries())).observe({
      type,
      buffered: true,
      ...extra,
    });
  } catch {
    // An entry type the browser lists but refuses to observe: skip that metric.
  }
}

let started = false;

/**
 * Start collecting. Safe to call more than once; only the first call counts.
 * `onReported` runs after the last metric of the page load has been handed to
 * `report`, so the caller can push its telemetry out before the page goes.
 */
export function startWebVitals(report: VitalReporter, onReported?: () => void): void {
  if (started || typeof window === "undefined" || typeof PerformanceObserver === "undefined") {
    return;
  }
  started = true;

  // A tab opened in the background paints whenever the browser gets round to
  // it, so its paint timings measure the browser's scheduling, not the app.
  const loadedHidden = document.visibilityState === "hidden";

  let fcp: number | undefined;
  let lcp: number | undefined;
  let inp: number | undefined;
  const shifts: LayoutShiftEntry[] = [];

  observe("paint", (entries) => {
    const entry = entries.find((e) => e.name === "first-contentful-paint");
    if (entry) fcp = entry.startTime;
  });
  observe("largest-contentful-paint", (entries) => {
    const entry = entries[entries.length - 1];
    if (entry) lcp = entry.startTime;
  });
  observe("layout-shift", (entries) => {
    shifts.push(...(entries as LayoutShiftEntry[]));
  });
  observe(
    "event",
    (entries) => {
      for (const entry of entries as EventTimingEntry[]) {
        if (!entry.interactionId) continue;
        inp = Math.max(inp ?? 0, entry.duration);
      }
    },
    { durationThreshold: 40 },
  );

  // `pagehide` counts even while the page still reads as visible: Safari
  // fires it before (or instead of) the visibility change on close.
  const flush = (event: Event) => {
    if (event.type === "visibilitychange" && document.visibilityState !== "hidden") return;
    document.removeEventListener("visibilitychange", flush, true);
    window.removeEventListener("pagehide", flush, true);

    const navigation = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    const properties: Record<string, string> = {
      route: routeTemplate(window.location.pathname),
      navigationType: navigation?.type ?? "unknown",
    };

    if (navigation && navigation.responseStart > 0) {
      report("TTFB", navigation.responseStart, properties);
    }
    if (!loadedHidden && fcp !== undefined) report("FCP", fcp, properties);
    if (!loadedHidden && lcp !== undefined) report("LCP", lcp, properties);
    if (PerformanceObserver.supportedEntryTypes?.includes("layout-shift")) {
      report("CLS", clsFromShifts(shifts), properties);
    }
    if (inp !== undefined) report("INP", inp, properties);
    onReported?.();
  };

  document.addEventListener("visibilitychange", flush, true);
  window.addEventListener("pagehide", flush, true);
}
